import React, { useState, useEffect } from 'react';
import { bool, object, objectOf, oneOfType, string } from 'prop-types';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import classNames from 'classnames/bind';
import ProductSearch from 'yoda-site-components/lib/components/ProductSearch/ProductSearch';
import HelpSection from 'yoda-site-components/lib/components/YodaFooter/components/HelpSection';
import CertonaZone from 'yoda-site-components/lib/components/CertonaZone/CertonaZone';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import {
    selectPreferences,
    selectIsNative,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import isBloomReachAudience, {
    enableBloomReachPage,
} from 'yoda-site-components/lib/helpers/BloomReach/BloomReach';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import * as styles from './NoSearchResults.css';
import { noSearchResult } from '../../common/constants';
import { getURLParameterBykey } from '../../utils';

const cx = classNames.bind(styles);

const NoSearchResults = (props) => {
    const {
        commonData: { gallery },
        featureFlags: {
            enableV2URL = true,
            isDisplaySRPMedaliaSurvey,
            enableBopisNoSearchBloomreach = true,
            enableBloomReach = false,
            enableBloomReachV3PLP = true,
            enableBloomReachNative = false,
        },
        location,
        preferences,
        isNative,
        requestUrl,
    } = props;

    const {
        findAStore,
        noProductsFound,
        searchTips,
        suggestionList,
        suggestionWithStoreLink,
        tryNewKeyword,
    } = noSearchResult;

    const [mounted, setMounted] = useState(false);

    if (!mounted) {
        LocalStorage.setData('NoResultsPage', true);
    }

    useEffect(() => {
        setMounted(true);
    }, []);

    const storeAvailabilityFilter = useSelector((state) =>
        _get(state?.bopisInfo, 'selectedStores', [])
    );

    const isSelectedStoreAvailable = storeAvailabilityFilter && storeAvailabilityFilter.length > 0;

    const footerHelpSection = _get(preferences, 'footer.HelpSection', {});
    const noSearchResultBloomreachHead = _get(preferences, 'noSearchResultBloomreachHead', '');
    const noSearchResultBloomreacFilter = _get(preferences, 'noSearchResultBloomreacFilter', '');
    const plpBloomreachInclusionList = _get(preferences, 'plpBloomreachInclusionList', []);
    const storesId = getURLParameterBykey('storeIds');
    const isBloomReachV3Page = enableBloomReachPage(
        enableBloomReachV3PLP,
        requestUrl,
        plpBloomreachInclusionList
    );
    const isNativeBloomReach = enableBloomReachNative ? false : isNative;
    const bloomReachAudience = isBloomReachAudience(enableBloomReach, isNativeBloomReach);
    const slotDetails = {
        attributes: { recommendations: true },
        loader: 'certona',
        pageType: 'NOSEARCH',
        slotId: 'nosearch1_rr',
    };

    if (!gallery) return null;

    const { Ntt, searchTerm } = makeQuery(location.search);

    const assistanceContainerClasses = `${dt(['py-10', 'px-16', 'bg-light-white'])} ${cx(
        'assistance-container'
    )}`;

    const assistanceListClasses = `${dt(['block'])} ${cx('assistance-list')}`;

    const listSectionClasses = dt([
        'mb-4',
        'text-black',
        'w-full',
        'sm:block',
        'md:table-cell',
        'md:align-top',
        'md:w-7/12',
    ]);

    const searchTipsClasses = dt([
        'capitalize',
        'font-open-sans-bold',
        'text-medium',
        'text-black',
        'sm:block',
        'md:table-row',
        'md:w-full',
    ]);

    const suggestionWrapperClasses = `${dt([
        'flex',
        'sm:flex-col',
        'sm:flex-row',
        'md:items-start',
        'md:flex-row',
        'gap-x-4',
        'w-full',
    ])} ${cx('suggestion-wrapper')}`;

    const titleClasses = dt(['font-open-sans-bold', 'text-black', 'text-xlarge']);

    const wrapperClasses = dt(['mb-6']);

    const displayMedaliaSurvey = (isDisplayPLPMedaliaSurvey) => {
        // added !__SERVER_ condition on serverside srp div id is replacing with plp medalia div id
        if (isDisplayPLPMedaliaSurvey && !__SERVER__) {
            return (
                <div
                    id="srp_survey_inline"
                    className={dt(['py-2', 'pl-2', 'relative', 'w-full'])}
                    data-automation-id="srp_survey_link"
                />
            );
        }
        return '';
    };

    return (
        <>
            <div
                data-cnstrc-search=""
                data-cnstrc-search-term={getURLParameterBykey('searchTerm') || searchTerm || Ntt}
                data-cnstrc-num-results="0"
                data-cnstrc-result-page="1"
            />
            <div className={wrapperClasses}>
                <div className={dt(['m-auto', 'md:max-w-[720px]', 'lg:max-w-[922px]'])}>
                    {enableBopisNoSearchBloomreach &&
                    (bloomReachAudience || isBloomReachV3Page) &&
                    (!isEmpty(storesId) || isSelectedStoreAvailable) ? (
                        <div className={dt(['text-center'])}>
                            <h2 className={dt(['font-open-sans-bold', 'leading-8', 'text-[20px]'])}>
                                {noSearchResultBloomreachHead}
                            </h2>
                            <p className={dt(['leading-6', 'text-[16px]'])}>
                                {noSearchResultBloomreacFilter}
                            </p>
                        </div>
                    ) : (
                        <h2 className={titleClasses}>
                            {`${noProductsFound} ${enableV2URL ? searchTerm : Ntt}`}
                        </h2>
                    )}
                    <div
                        className={dt([
                            'font-open-sans-bold',
                            'mb-4',
                            'mt-4',
                            'text-medium',
                            'text-black',
                        ])}
                    >
                        {tryNewKeyword}
                    </div>
                    <div className={`${dt(['mb-6'])} ${cx('noSearchResultsPageSearch')}`}>
                        <ProductSearch
                            automationId="tablet-search-block"
                            isNoResultsProductSearch
                        />
                    </div>
                    <div className={suggestionWrapperClasses}>
                        <div className={listSectionClasses}>
                            <div className={searchTipsClasses}>{searchTips}</div>
                            <ul className={dt(['list-disc', 'pl-4', 'text-medium', 'mb-6'])}>
                                {suggestionList.map((suggestion) => (
                                    <li className={dt(['mb-4', 'mt-4'])} key={suggestion.id}>
                                        {suggestion.text}
                                    </li>
                                ))}
                                <li className={dt(['mb-4', 'mt-4'])}>
                                    <span className={dt(['mr-0.5'])}>
                                        {suggestionWithStoreLink}
                                    </span>
                                    <a
                                        href="/findastore"
                                        className={dt([
                                            'active:underline',
                                            'focus:underline',
                                            'hover:underline',
                                            'text-black',
                                            'underline',
                                            'visited:underline',
                                        ])}
                                    >
                                        {findAStore}
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div className={assistanceContainerClasses}>
                            <HelpSection
                                automationId="footer-help"
                                footerHelpSection={footerHelpSection}
                                listClass={assistanceListClasses}
                            />
                        </div>
                    </div>
                    {displayMedaliaSurvey(isDisplaySRPMedaliaSurvey)}
                </div>
            </div>
            <div className={wrapperClasses}>
                <CertonaZone
                    page={slotDetails.pageType}
                    slotDetails={slotDetails}
                    schemasToRender={slotDetails.slotId}
                />
            </div>
        </>
    );
};

NoSearchResults.propTypes = {
    commonData: oneOfType([bool, object, string]).isRequired,
    featureFlags: objectOf(bool).isRequired,
    preferences: objectOf(object).isRequired,
    location: objectOf(object),
};

NoSearchResults.defaultProps = {
    location: {},
};

const mapStateToProps = (store) => ({
    commonData: store.commonData,
    deviceType: store.context.deviceType,
    featureFlags: store.context.featureFlags,
    preferences: selectPreferences(store),
    isNative: selectIsNative(store),
    requestUrl: store.context.requestUrl,
});

export default withRouter(connect(mapStateToProps)(NoSearchResults));
